import { useState, type FormEvent } from 'react'
import { createPortal } from 'react-dom'
import { FileEdit, Loader2, Sparkles } from 'lucide-react'
import { api, ApiError } from '../../lib/api'
import AiResultModal from '../ui/AiResultModal'
import type { AiDraftRequest, AiDraftResponse, AiSummaryResponse } from '@toga/shared'

const DRAFT_KINDS: Array<{ k: AiDraftRequest['kind']; label: string }> = [
  { k: 'DEMANDA', label: 'Demanda' },
  { k: 'CONTESTACION', label: 'Contestación' },
  { k: 'RECURSO', label: 'Recurso' },
  { k: 'ESCRITO', label: 'Escrito procesal' },
  { k: 'CORREO_CLIENTE', label: 'Correo al cliente' },
  { k: 'CORREO_PROCURADOR', label: 'Correo al procurador' },
  { k: 'OTRO', label: 'Otro' },
]

interface Props {
  caseId: string
  caseCode: string
}

export default function CaseAiPanel({ caseId, caseCode }: Props) {
  const [busy, setBusy] = useState<'none' | 'summary' | 'draft'>('none')
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<{
    title: string
    text: string
    conversationId: string | null
  } | null>(null)
  const [showDraft, setShowDraft] = useState(false)

  const onSummary = async () => {
    setBusy('summary')
    setError(null)
    try {
      const resp = await api.post<AiSummaryResponse>('/ai/summary', {
        contextType: 'CASE',
        contextId: caseId,
        style: 'BRIEF',
      })
      setResult({
        title: `Resumen — Expediente ${caseCode}`,
        text: resp.text,
        conversationId: resp.conversationId,
      })
    } catch (err) {
      setError(extractError(err))
    } finally {
      setBusy('none')
    }
  }

  return (
    <div className="surface p-5">
      <h3 className="t-primary font-semibold mb-3 flex items-center gap-2">
        <Sparkles size={14} className="text-toga-400" />
        Asistente IA
      </h3>
      <div className="space-y-2">
        <button
          type="button"
          onClick={onSummary}
          disabled={busy !== 'none'}
          className="w-full text-left px-3 py-2 rounded-lg border border-base hover:bg-hover text-sm t-secondary disabled:opacity-50 flex items-center gap-2"
        >
          {busy === 'summary' ? (
            <Loader2 size={14} className="animate-spin" />
          ) : (
            <Sparkles size={14} className="text-toga-400" />
          )}
          Resumir expediente
        </button>
        <button
          type="button"
          onClick={() => setShowDraft(true)}
          disabled={busy !== 'none'}
          className="w-full text-left px-3 py-2 rounded-lg border border-base hover:bg-hover text-sm t-secondary disabled:opacity-50 flex items-center gap-2"
        >
          <FileEdit size={14} className="text-toga-400" />
          Redactar escrito…
        </button>
      </div>
      {error && <p className="mt-3 text-xs text-rose-300">{error}</p>}

      {result && (
        <AiResultModal
          title={result.title}
          text={result.text}
          conversationId={result.conversationId}
          onClose={() => setResult(null)}
        />
      )}

      {showDraft && (
        <DraftModal
          caseId={caseId}
          onClose={() => setShowDraft(false)}
          onDone={(r) => {
            setShowDraft(false)
            setResult(r)
          }}
        />
      )}
    </div>
  )
}

interface DraftModalProps {
  caseId: string
  onClose: () => void
  onDone: (r: { title: string; text: string; conversationId: string }) => void
}

function DraftModal({ caseId, onClose, onDone }: DraftModalProps) {
  const [kind, setKind] = useState<AiDraftRequest['kind']>('ESCRITO')
  const [instructions, setInstructions] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (instructions.trim().length < 5) {
      setError('Indica al menos unas instrucciones breves.')
      return
    }
    setBusy(true)
    setError(null)
    try {
      const resp = await api.post<AiDraftResponse>('/ai/draft', {
        caseId,
        kind,
        instructions: instructions.trim(),
      })
      const kindLabel = DRAFT_KINDS.find((d) => d.k === kind)?.label ?? kind
      onDone({
        title: `Borrador — ${kindLabel}`,
        text: resp.text,
        conversationId: resp.conversationId,
      })
    } catch (err) {
      setError(extractError(err))
    } finally {
      setBusy(false)
    }
  }

  return createPortal(
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm grid place-items-center px-4"
      onClick={onClose}
    >
      <form
        onSubmit={onSubmit}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg surface-deep p-5 animate-slideUp space-y-3"
      >
        <h3 className="text-base font-semibold t-primary">Redactar escrito</h3>
        <div>
          <label className="block text-xs t-muted mb-1">Tipo</label>
          <select
            value={kind}
            onChange={(e) => setKind(e.target.value as AiDraftRequest['kind'])}
            className="w-full px-3 py-2 rounded-lg bg-slate-900/60 border border-slate-700/60 text-slate-100 focus:outline-none focus:ring-1 focus:ring-toga-500/40"
          >
            {DRAFT_KINDS.map((d) => (
              <option key={d.k} value={d.k}>
                {d.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs t-muted mb-1">Instrucciones</label>
          <textarea
            value={instructions}
            onChange={(e) => setInstructions(e.target.value)}
            rows={6}
            placeholder="P. ej. Recurso de reposición contra el auto de admisión de prueba documental por defecto formal del art. ..."
            className="w-full px-3 py-2 rounded-lg bg-slate-900/60 border border-slate-700/60 text-slate-100 focus:outline-none focus:ring-1 focus:ring-toga-500/40"
          />
          <p className="text-[10px] t-faint mt-1">
            La IA recibirá la ficha del expediente como contexto. Devolverá un borrador en Markdown
            para revisar antes de firmar.
          </p>
        </div>
        {error && <p className="text-xs text-rose-300">{error}</p>}
        <div className="flex items-center justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 rounded-lg text-sm t-secondary border border-base hover:bg-hover"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={busy}
            className="px-3 py-1.5 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-toga-600 via-toga-500 to-toga-700 hover:shadow-glow-toga disabled:opacity-50 flex items-center gap-1.5"
          >
            {busy && <Loader2 size={14} className="animate-spin" />}
            Generar
          </button>
        </div>
      </form>
    </div>,
    document.body,
  )
}

function extractError(err: unknown): string {
  if (err instanceof ApiError) {
    const body = err.body as { error?: string; code?: string } | undefined
    if (body?.code === 'AI_NOT_CONFIGURED') {
      return 'Asistente IA no configurado en el servidor.'
    }
    return body?.error ?? err.message
  }
  return err instanceof Error ? err.message : 'Error inesperado'
}
